import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import type { ReactNode } from "react";

interface KebabProps {
  /** Render menu items; `close` lets an item dismiss the menu after acting. */
  children: (close: () => void) => ReactNode;
  disabled?: boolean;
  label?: string;
}

/**
 * Icon button + dropdown menu. The menu is rendered in a portal with fixed
 * positioning so it is never clipped by an ancestor's `overflow-hidden` (e.g. the
 * rounded contents card) and flips above the button when there isn't room below —
 * which is what made the Download action invisible on the last row.
 */
export function Kebab({ children, disabled = false, label = "Row actions" }: KebabProps) {
  const [open, setOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; right: number } | null>(null);

  const close = () => setOpen(false);

  // Position the fixed menu relative to the trigger; flip up near the viewport edge.
  useLayoutEffect(() => {
    if (!open || !buttonRef.current) {
      return;
    }
    const rect = buttonRef.current.getBoundingClientRect();
    const menuHeight = menuRef.current?.offsetHeight ?? 48;
    const openUp = window.innerHeight - rect.bottom < menuHeight + 12;
    setPos({
      top: openUp ? Math.max(8, rect.top - menuHeight - 4) : rect.bottom + 4,
      right: Math.max(8, window.innerWidth - rect.right),
    });
  }, [open]);

  useEffect(() => {
    if (!open) {
      return;
    }
    const onDocMouseDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (buttonRef.current?.contains(target) || menuRef.current?.contains(target)) {
        return;
      }
      setOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setOpen(false);
      }
    };
    // A fixed-positioned menu shouldn't drift when the page scrolls/resizes.
    const onReflow = () => setOpen(false);
    document.addEventListener("mousedown", onDocMouseDown);
    document.addEventListener("keydown", onKeyDown);
    window.addEventListener("scroll", onReflow, true);
    window.addEventListener("resize", onReflow);
    return () => {
      document.removeEventListener("mousedown", onDocMouseDown);
      document.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("scroll", onReflow, true);
      window.removeEventListener("resize", onReflow);
    };
  }, [open]);

  // Reset measured position when closing so the next open re-measures.
  useEffect(() => {
    if (!open) {
      setPos(null);
    }
  }, [open]);

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={open}
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
        className="inline-flex h-8 w-8 items-center justify-center rounded-md text-text-muted hover:bg-surface-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 disabled:cursor-not-allowed disabled:opacity-40"
      >
        <svg viewBox="0 0 24 24" className="h-5 w-5" fill="currentColor" aria-hidden="true">
          <circle cx="12" cy="5" r="1.6" />
          <circle cx="12" cy="12" r="1.6" />
          <circle cx="12" cy="19" r="1.6" />
        </svg>
      </button>
      {open
        ? createPortal(
            <div
              ref={menuRef}
              role="menu"
              style={{
                position: "fixed",
                top: pos?.top ?? -9999,
                right: pos?.right ?? 0,
                visibility: pos ? "visible" : "hidden",
              }}
              className="z-50 min-w-[10rem] overflow-hidden rounded-md border border-surface-border bg-surface py-1 shadow-lg"
            >
              {children(close)}
            </div>,
            document.body,
          )
        : null}
    </>
  );
}

interface KebabItemProps {
  onClick?: () => void;
  href?: string;
  download?: boolean;
  children: ReactNode;
}

/** A single menu row; renders as an anchor when `href` is supplied. */
export function KebabItem({ onClick, href, download, children }: KebabItemProps) {
  const className =
    "flex w-full items-center gap-2 px-3 py-2 text-left text-body text-text hover:bg-surface-muted";
  if (href) {
    return (
      <a role="menuitem" href={href} download={download} onClick={onClick} className={className}>
        {children}
      </a>
    );
  }
  return (
    <button role="menuitem" type="button" onClick={onClick} className={className}>
      {children}
    </button>
  );
}
